import React, { useMemo } from 'react';
import { Calendar, Info } from 'lucide-react';
import { CalculatedMetrics } from '../../types';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';

interface SeasonalityAnalysisProps {
  data: CalculatedMetrics[];
}

interface TimePattern {
  label: string;
  avgSpike: number;
  count: number;
  winRate: number;
  avgRetracement: number;
}

export const SeasonalityAnalysis: React.FC<SeasonalityAnalysisProps> = ({ data }) => {
  const { dayOfWeek, monthlyPatterns, yearlyTrends } = useMemo(() => {
    if (data.length === 0) {
      return {
        dayOfWeek: [],
        monthlyPatterns: [],
        yearlyTrends: []
      };
    }

    const dayNames = ['Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'];
    const monthNames = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];

    const dayGroups = Array.from({ length: 7 }, (_, i) => {
      const dayData = data.filter(d => d.Date.getDay() === i);
      if (dayData.length === 0) {
        return {
          label: dayNames[i],
          avgSpike: 0,
          count: 0,
          winRate: 0,
          avgRetracement: 0
        };
      }

      const avgSpike = dayData.reduce((sum, d) => sum + (d['Spike %'] || 0) * 100, 0) / dayData.length;
      const redCloses = dayData.filter(d => d['Red Close'] === 1).length;
      const winRate = (redCloses / dayData.length) * 100;
      const avgRetracement = dayData.reduce((sum, d) => {
        const ret = ((d.Close - d.High) / d.High) * 100;
        return sum + (isFinite(ret) ? Math.abs(ret) : 0);
      }, 0) / dayData.length;

      return {
        label: dayNames[i],
        avgSpike,
        count: dayData.length,
        winRate,
        avgRetracement
      };
    });

    const monthGroups = Array.from({ length: 12 }, (_, i) => {
      const monthData = data.filter(d => d.Date.getMonth() === i);
      if (monthData.length === 0) {
        return {
          label: monthNames[i],
          avgSpike: 0,
          count: 0,
          winRate: 0,
          avgRetracement: 0
        };
      }

      const avgSpike = monthData.reduce((sum, d) => sum + (d['Spike %'] || 0) * 100, 0) / monthData.length;
      const redCloses = monthData.filter(d => d['Red Close'] === 1).length;
      const winRate = (redCloses / monthData.length) * 100;
      const avgRetracement = monthData.reduce((sum, d) => {
        const ret = ((d.Close - d.High) / d.High) * 100;
        return sum + (isFinite(ret) ? Math.abs(ret) : 0);
      }, 0) / monthData.length;

      return {
        label: monthNames[i],
        avgSpike,
        count: monthData.length,
        winRate,
        avgRetracement
      };
    });

    const yearGroups = new Map<number, CalculatedMetrics[]>();
    data.forEach(d => {
      const year = d.Date.getFullYear();
      if (!yearGroups.has(year)) {
        yearGroups.set(year, []);
      }
      yearGroups.get(year)!.push(d);
    });

    const yearlyData: TimePattern[] = Array.from(yearGroups.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([year, yearData]) => {
        const avgSpike = yearData.reduce((sum, d) => sum + (d['Spike %'] || 0) * 100, 0) / yearData.length;
        const redCloses = yearData.filter(d => d['Red Close'] === 1).length;
        const winRate = (redCloses / yearData.length) * 100;
        const avgRetracement = yearData.reduce((sum, d) => {
          const ret = ((d.Close - d.High) / d.High) * 100;
          return sum + (isFinite(ret) ? Math.abs(ret) : 0);
        }, 0) / yearData.length;

        return {
          label: year.toString(),
          avgSpike,
          count: yearData.length,
          winRate,
          avgRetracement
        };
      });

    return {
      dayOfWeek: dayGroups,
      monthlyPatterns: monthGroups,
      yearlyTrends: yearlyData
    };
  }, [data]);

  const renderPatternTable = (patterns: TimePattern[], title: string) => {
    const maxAvgSpike = Math.max(...patterns.map(p => p.avgSpike), 1);

    return (
      <div className="bg-gray-800/50 border border-gray-600 rounded-lg p-4 mb-6">
        <h5 className="text-sm font-medium text-white mb-4">{title}</h5>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-gray-600">
                <th className="text-left py-2 text-gray-400 font-medium">Periodo</th>
                <th className="text-center py-2 text-gray-400 font-medium">Conteggio</th>
                <th className="text-center py-2 text-gray-400 font-medium">Spike Medio</th>
                <th className="text-center py-2 text-gray-400 font-medium">Win Rate</th>
                <th className="text-center py-2 text-gray-400 font-medium">Ritracciamento</th>
              </tr>
            </thead>
            <tbody>
              {patterns.map((pattern, index) => (
                <tr key={index} className="border-b border-gray-700">
                  <td className="py-2 text-white font-medium">{pattern.label}</td>
                  <td className="text-center py-2 text-gray-300">{pattern.count}</td>
                  <td className="py-2">
                    <div className="flex items-center space-x-2">
                      <div className="flex-1 h-6 bg-gray-700 rounded overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center"
                          style={{ width: `${(pattern.avgSpike / maxAvgSpike) * 100}%` }}
                        >
                          <span className="text-xs font-bold text-white px-2">
                            {pattern.avgSpike.toFixed(1)}%
                          </span>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="text-center py-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      pattern.winRate >= 75
                        ? 'bg-green-400/20 text-green-400'
                        : pattern.winRate >= 65
                        ? 'bg-yellow-400/20 text-yellow-400'
                        : 'bg-red-400/20 text-red-400'
                    }`}>
                      {pattern.winRate.toFixed(1)}%
                    </span>
                  </td>
                  <td className="text-center py-2 text-red-400 font-medium">
                    {pattern.avgRetracement.toFixed(1)}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-gray-900/30 border border-gray-600 rounded-lg p-4 sm:p-6">
      <div className="flex items-center space-x-2 sm:space-x-3 mb-6">
        <div className="p-1.5 sm:p-2 bg-orange-400/10 rounded-lg">
          <Calendar className="h-4 w-4 sm:h-5 sm:w-5 text-orange-400" />
        </div>
        <div>
          <h4 className="text-base sm:text-lg font-semibold text-white">Analisi della Stagionalità</h4>
          <p className="text-xs sm:text-sm text-gray-400">
            Pattern temporali e tendenze stagionali degli spike
          </p>
        </div>
      </div>

      {renderPatternTable(dayOfWeek, 'Pattern per Giorno della Settimana')}
      {renderPatternTable(monthlyPatterns, 'Pattern Mensili')}
      {renderPatternTable(yearlyTrends, 'Trend Annuali')}

      <div className="bg-blue-400/10 border border-blue-400/30 rounded-lg p-4">
        <div className="flex items-start space-x-2">
          <Info className="h-4 w-4 text-blue-400 flex-shrink-0 mt-0.5" />
          <div>
            <h5 className="text-sm font-medium text-blue-400">Interpretazione</h5>
            <ul className="text-xs text-blue-300 mt-2 space-y-1">
              <li>• Giorni/mesi con win rate alto sono periodi favorevoli per strategie short</li>
              <li>• Spike medi più alti indicano maggiore volatilità in quel periodo</li>
              <li>• Ritracciamenti maggiori suggeriscono opportunità di profitto più ampie</li>
              <li>• Considera il conteggio per valutare la significatività statistica dei pattern</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};
